import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { functionsService } from "@/services/functionsService";
import { useNavigate } from "react-router-dom";

interface EarningsItem {
  date: string;
  symbol: string;
  eps: number | null;
  epsEstimated: number | null;
  time: string;
  revenue: number | null;
  revenueEstimated: number | null;
  fiscalDateEnding: string;
  reportStatus?: "scheduled" | "awaiting_update" | "reported";
}
  const statusLabel = (status?: EarningsItem["reportStatus"]) => {
    if (status === "reported") return "Reported";
    if (status === "awaiting_update") return "Awaiting Update";
    return "Scheduled";
  };


export const EarningsCalendar: React.FC = () => {
  const [calendar, setCalendar] = useState<EarningsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: new Date().toISOString().split("T")[0],
    to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadCalendar();
  }, [dateRange]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await functionsService.getEarningsCalendar(
        dateRange.from,
        dateRange.to
      );
      if (result.success) {
        setCalendar(Array.isArray(result.calendar) ? result.calendar : []);
      } else {
        setError("Could not load earnings calendar.");
      }
    } catch (err) {
      console.error("Failed to load earnings calendar:", err);
      setError("Failed to load earnings calendar. Please try again.");
      setCalendar([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "N/A";
    if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toFixed(2)}`;
  };

  const getEpsColor = (eps: number | null | undefined, estimated: number | null | undefined) => {
    if (eps === null || eps === undefined || estimated === null || estimated === undefined) return "";
    if (eps >= estimated) return "text-green-600";
    return "text-red-600";
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-4">Earnings Calendar</h1>
          <div className="flex gap-4 items-center">
            <div>
              <label className="body-text text-sm text-gray-600 mb-1 block">
                From
              </label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) =>
                  setDateRange({ ...dateRange, from: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="body-text text-sm text-gray-600 mb-1 block">
                To
              </label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) =>
                  setDateRange({ ...dateRange, to: e.target.value })
                }
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Card className="p-6">
            <div className="animate-pulse">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </Card>
        ) : error ? (
          <Card className="p-6">
            <p className="body-text text-red-600 mb-2">{error}</p>
            <button
              type="button"
              onClick={loadCalendar}
              className="text-blazerex-green hover:underline body-text"
            >
              Try again
            </button>
          </Card>
        ) : calendar.length === 0 ? (
          <Card className="p-6">
            <p className="body-text text-gray-500">No earnings scheduled for this date range.</p>
          </Card>
        ) : (
          <Card className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 body-text font-header-bold">
                      Date
                    </th>
                    <th className="text-left py-3 px-4 body-text font-header-bold">
                      Symbol
                    </th>
                    <th className="text-right py-3 px-4 body-text font-header-bold">
                      EPS (Est.)
                    </th>
                    <th className="text-right py-3 px-4 body-text font-header-bold">
                      EPS (Actual)
                    </th>
                    <th className="text-right py-3 px-4 body-text font-header-bold">
                      Revenue (Est.)
                    </th>
                    <th className="text-right py-3 px-4 body-text font-header-bold">
                      Revenue (Actual)
                    </th>
                    <th className="text-center py-3 px-4 body-text font-header-bold">
                      Time
                    </th>
                    <th className="text-center py-3 px-4 body-text font-header-bold">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {calendar.map((item, index) => (
                    <tr
                      key={index}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/companies/${item.symbol}`)}
                    >
                      <td className="py-3 px-4 body-text">
                        {new Date(item.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </td>
                      <td className="py-3 px-4 body-text font-mono font-header-bold">
                        {item.symbol}
                      </td>
                      <td className="text-right py-3 px-4 body-text">
                        {item.epsEstimated !== null && item.epsEstimated !== undefined
                          ? `$${item.epsEstimated.toFixed(2)}`
                          : "N/A"}
                      </td>
                      <td
                        className={`text-right py-3 px-4 body-text font-header-bold ${getEpsColor(
                          item.eps,
                          item.epsEstimated
                        )}`}
                      >
                        {item.eps !== null && item.eps !== undefined ? `$${item.eps.toFixed(2)}` : "N/A"}
                      </td>
                      <td className="text-right py-3 px-4 body-text">
                        {formatCurrency(item.revenueEstimated)}
                      </td>
                      <td className="text-right py-3 px-4 body-text font-header-bold">
                        {formatCurrency(item.revenue)}
                      </td>
                      <td className="text-center py-3 px-4 body-text">
                        {item.time || "N/A"}
                      </td>
                      <td className="text-center py-3 px-4 body-text">
                        {statusLabel(item.reportStatus)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

